import { useState } from 'react';
import { ApiError, download, request } from '../api';
import { BarChart, dateTime, HBars, Loadable, money, Stat, todayIso, useLoad } from '../ui';

export type StoreRef = { id: string; name: string; currency: string | null; role: string; online: boolean; last_sync_at: string | null; timezone: string; status?: 'APPROVED' | 'BLOCKED' | null; restaurant_id?: string | null; pos_devices?: number };

const q = (from: string, to: string) => `from=${from}&to=${to}`;

export function Dashboard({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/dashboard`), [store.id]);
  const cur = store.currency;
  return (
    <Loadable state={state}>
      {(res: any) => {
        const d = res.data;
        return (
          <>
            <p className="muted">Business day {d.businessDate} ({res.timezone}) · synced data · last sync {dateTime(store.last_sync_at)}</p>
            <div className="stats">
              <Stat label="Collected today" value={money(d.today.collected, cur)} sub={d.changePercent === null ? 'No sales yesterday' : `${d.changePercent >= 0 ? '▲' : '▼'} ${Math.abs(d.changePercent).toFixed(1)}% vs yesterday (${money(d.yesterday.collected, cur)})`} />
              <Stat label="Bills paid" value={d.today.bills} sub={`Average ${money(d.today.averageBill, cur)}`} />
              <Stat label="Orders" value={d.orders.count} sub={`${d.orders.cancelled} cancelled`} />
              <Stat label="Open orders" value={d.activeOrders} sub="Pending, preparing, ready or served" />
              <Stat label="Refunded today" value={money(d.today.refunded, cur)} />
            </div>
            <div className="grid2">
              <section className="panel">
                <h3>Payments today</h3>
                {d.paymentMethods.length ? <HBars rows={d.paymentMethods.map((p: any) => ({ label: p.method, value: p.total, sub: `${p.count}` }))} format={(v) => money(v, cur)} /> : <p className="muted">No payments yet today.</p>}
              </section>
              <section className="panel">
                <h3>Top items today</h3>
                {d.topItems.length ? <HBars rows={d.topItems.map((i: any) => ({ label: i.name, value: i.revenue, sub: `${i.quantity} sold` }))} format={(v) => money(v, cur)} /> : <p className="muted">No paid items yet today.</p>}
              </section>
            </div>
          </>
        );
      }}
    </Loadable>
  );
}

export function Reports({ store }: { store: StoreRef }) {
  const [from, setFrom] = useState(todayIso(-6));
  const [to, setTo] = useState(todayIso());
  const cur = store.currency;
  const summary = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/summary?${q(from, to)}`), [store.id, from, to]);
  const daily = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/daily?${q(from, to)}`), [store.id, from, to]);
  const hourly = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/hourly?${q(from, to)}`), [store.id, from, to]);
  const items = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/items?${q(from, to)}&limit=15`), [store.id, from, to]);
  const categories = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/categories?${q(from, to)}`), [store.id, from, to]);
  const staff = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/staff?${q(from, to)}`).catch((e) => (e instanceof ApiError && e.status === 403 ? { data: null } : Promise.reject(e))), [store.id, from, to]);
  const refunds = useLoad(() => request('GET', `/v1/stores/${store.id}/reports/refunds?${q(from, to)}`).catch((e) => (e instanceof ApiError && e.status === 403 ? { data: null } : Promise.reject(e))), [store.id, from, to]);
  const preset = (days: number) => {
    setFrom(todayIso(-(days - 1)));
    setTo(todayIso());
  };
  return (
    <>
      <div className="filters">
        <label>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="chip" onClick={() => preset(1)}>Today</button>
        <button className="chip" onClick={() => preset(7)}>7 days</button>
        <button className="chip" onClick={() => preset(30)}>30 days</button>
        <span className="spacer" />
        {['orders', 'bills', 'refunds'].map((k) => (
          <button key={k} className="btn small secondary" onClick={() => download(`/v1/stores/${store.id}/export/${k}?${q(from, to)}`, `${k}-${from}-${to}.csv`).catch((e) => alert(e.message))}>Export {k}</button>
        ))}
      </div>
      <Loadable state={summary}>
        {(res: any) => {
          const f = res.data.financial;
          return (
            <>
              <div className="stats">
                <Stat label="Net collected" value={money(f.netCollected, cur)} sub={`Gross ${money(f.grossCollected, cur)} − refunds ${money(f.refunded, cur)}`} />
                <Stat label="Bills" value={f.billCount} sub={`Average ${money(f.averageOrderValue, cur)}`} />
                <Stat label="Tax collected" value={money(f.taxCollected, cur)} />
                <Stat label="Discounts" value={money(f.discounts, cur)} />
                <Stat label="Orders" value={res.data.orders.count} sub={`${res.data.orders.cancelled} cancelled`} />
              </div>
              <p className="muted small">Same rules as the POS financial summary: bills by paid date, refunds attributed to the refunded bill's day, business days in {res.timezone}.</p>
              <div className="grid2">
                <section className="panel">
                  <h3>Payment methods</h3>
                  <HBars rows={f.paymentMethods.map((p: any) => ({ label: p.method, value: p.total, sub: `${p.count}` }))} format={(v) => money(v, cur)} />
                </section>
                <section className="panel">
                  <h3>Order types</h3>
                  <HBars rows={res.data.orders.byType.map((t: any) => ({ label: `${t.type ?? 'unknown'}${t.source && t.source !== 'pos' ? ` (${t.source})` : ''}`, value: t.total, sub: `${t.count}` }))} format={(v) => money(v, cur)} />
                </section>
              </div>
            </>
          );
        }}
      </Loadable>
      <div className="grid2">
        <section className="panel">
          <h3>Collected per day</h3>
          <Loadable state={daily}>
            {(res: any) => (
              <>
                <BarChart label="Collected per business day" data={res.data.map((d: any) => ({ key: d.date, value: d.collected }))} format={(v) => money(v, cur)} />
                <details><summary>Table</summary><table><thead><tr><th>Date</th><th>Bills</th><th>Refunded</th><th>Collected</th></tr></thead><tbody>{res.data.map((d: any) => <tr key={d.date}><td>{d.date}</td><td className="num">{d.bills}</td><td className="num">{money(d.refunded, cur)}</td><td className="num">{money(d.collected, cur)}</td></tr>)}</tbody></table></details>
              </>
            )}
          </Loadable>
        </section>
        <section className="panel">
          <h3>Busy hours (local time)</h3>
          <Loadable state={hourly}>
            {(res: any) => (
              <>
                <BarChart label="Sales by hour" data={res.data.map((h: any) => ({ key: `${String(h.hour).padStart(2, '0')}:00`, value: h.sales }))} format={(v) => money(v, cur)} />
                <details><summary>Table</summary><table><thead><tr><th>Hour</th><th>Orders</th><th>Bills</th><th>Sales</th></tr></thead><tbody>{res.data.filter((h: any) => h.orders || h.bills).map((h: any) => <tr key={h.hour}><td>{String(h.hour).padStart(2, '0')}:00</td><td className="num">{h.orders}</td><td className="num">{h.bills}</td><td className="num">{money(h.sales, cur)}</td></tr>)}</tbody></table></details>
              </>
            )}
          </Loadable>
        </section>
        <section className="panel">
          <h3>Top products</h3>
          <Loadable state={items} empty={(r: any) => !r.data.length}>
            {(res: any) => <HBars rows={res.data.map((i: any) => ({ label: i.name, value: i.revenue, sub: `${i.quantity} · ${i.category}` }))} format={(v) => money(v, cur)} />}
          </Loadable>
        </section>
        <section className="panel">
          <h3>Categories</h3>
          <Loadable state={categories} empty={(r: any) => !r.data.length}>
            {(res: any) => <HBars rows={res.data.map((c: any) => ({ label: c.category, value: c.revenue, sub: `${c.quantity}` }))} format={(v) => money(v, cur)} />}
          </Loadable>
        </section>
        <section className="panel">
          <h3>Staff</h3>
          <Loadable state={staff}>
            {(res: any) => (res.data ? <table><thead><tr><th>Staff</th><th>Orders</th><th>Value</th></tr></thead><tbody>{res.data.map((s: any) => <tr key={s.staffId ?? 'none'}><td>{s.name}</td><td className="num">{s.orders}</td><td className="num">{money(s.total, cur)}</td></tr>)}</tbody></table> : <p className="muted">Your role cannot view staff reports.</p>)}
          </Loadable>
        </section>
        <section className="panel">
          <h3>Refunds</h3>
          <Loadable state={refunds}>
            {(res: any) => (res.data ? (res.data.length ? <table><thead><tr><th>When</th><th>Order</th><th>Method</th><th>Amount</th></tr></thead><tbody>{res.data.map((r: any) => <tr key={r.id}><td>{dateTime(r.createdAt)}</td><td>{r.orderNumber}</td><td>{r.method}</td><td className="num">{money(r.amount, cur)}</td></tr>)}</tbody></table> : <p className="muted">No refunds in this range.</p>) : <p className="muted">Your role cannot view refund detail.</p>)}
          </Loadable>
        </section>
      </div>
    </>
  );
}

export function Orders({ store }: { store: StoreRef }) {
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const cur = store.currency;
  const state = useLoad(() => request('GET', `/v1/stores/${store.id}/orders?${q(from, to)}&page=${page}&page_size=25${status ? `&status=${status}` : ''}`), [store.id, from, to, page, status]);
  const detail = useLoad(() => (open ? request('GET', `/v1/stores/${store.id}/orders/${open}`) : Promise.resolve(null)), [store.id, open]);
  return (
    <>
      <div className="filters">
        <label>From <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} /></label>
        <label>To <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></label>
        <label>Status <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All</option>
          {['pending', 'preparing', 'ready', 'served', 'completed', 'cancelled'].map((s) => <option key={s}>{s}</option>)}
        </select></label>
      </div>
      <Loadable state={state} empty={(r: any) => !r.orders.length}>
        {(res: any) => (
          <>
            <table>
              <thead><tr><th>Order</th><th>Created</th><th>Type</th><th>Table</th><th>Status</th><th>Total</th><th>Paid</th></tr></thead>
              <tbody>
                {res.orders.map((o: any) => (
                  <tr key={o.id} className="clickable" onClick={() => setOpen(o.id)}>
                    <td>{o.order_number}{o.source === 'qr' && <span className="pill">QR</span>}</td>
                    <td>{dateTime(o.created_at)}</td><td>{o.type}</td><td>{o.table_label ?? 'n/a'}</td><td>{o.status}</td>
                    <td className="num">{money(o.total, cur)}</td><td className="num">{money(o.paid, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pager">
              <button className="btn small secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span className="muted">Page {res.page} of {Math.max(1, Math.ceil(res.total / res.page_size))} · {res.total} orders</span>
              <button className="btn small secondary" disabled={page * res.page_size >= res.total} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </>
        )}
      </Loadable>
      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(null)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button className="close" aria-label="Close" onClick={() => setOpen(null)}>×</button>
            <Loadable state={detail}>
              {(d: any) => d && (
                <>
                  <h3>Order {d.order.order_number}</h3>
                  <p className="muted">{d.order.type} · {d.order.status} · synced {dateTime(d.synced_at)}</p>
                  <table><thead><tr><th>Item</th><th>Qty</th><th>Total</th></tr></thead><tbody>{(d.order.items ?? []).map((i: any) => <tr key={i.id}><td>{i.product_name}</td><td className="num">{i.quantity}</td><td className="num">{money(Number(i.total), cur)}</td></tr>)}</tbody></table>
                  <p className="right"><strong>Total {money(Number(d.order.total), cur)}</strong></p>
                  {(d.order.bills ?? []).map((b: any) => <p key={b.id} className="muted small">Bill {b.bill_number}: {b.payment_status} {money(Number(b.paid_amount), cur)} {b.paid_at ? `· paid ${b.paid_at}` : ''}</p>)}
                  <p className="muted small">Customer details are never uploaded to the cloud.</p>
                </>
              )}
            </Loadable>
          </div>
        </div>
      )}
    </>
  );
}

export function LiveOrders({ store }: { store: StoreRef }) {
  const state = useLoad(() => request('POST', `/v1/stores/${store.id}/live`, { type: 'orders.live' }), [store.id]);
  return (
    <>
      <p className="muted">Read directly from the POS right now. Requires the POS to be online.</p>
      <button className="btn small secondary" onClick={state.reload}>Refresh</button>
      <Loadable state={state}>
        {(res: any) => (
          <table>
            <thead><tr><th>Order</th><th>Status</th><th>Table</th><th>Items</th><th>Total</th><th>Created</th></tr></thead>
            <tbody>{res.result.orders.map((o: any) => <tr key={o.id}><td>{o.order_number}</td><td>{o.status}</td><td>{o.table?.name ?? 'n/a'}</td><td>{(o.items ?? []).length}</td><td className="num">{money(Number(o.total), store.currency)}</td><td>{o.created_at}</td></tr>)}</tbody>
          </table>
        )}
      </Loadable>
    </>
  );
}
